<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Welcome to your virtual work experience induction for secondary school teaching!
      </p>
      <p class="mb-5">
        Through this experience you will get a taste for the theories and practices involved in
        being a great doctor
      </p>
      <p class="mb-5">In this experience, we will go through:</p>
      <ul class="default-list">
        <li>Different kinds of doctors</li>
        <li>What qualifications you need to get started as a doctor</li>
        <li>Insights into some of the core principles of practicing medicine</li>
        <li>Some exercises you can do to get a taste of what it’s like to be a doctor</li>
      </ul>
      <p>Let’s dive in!</p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <h4 class="mb-5">Let’s start your virtual job experience as a Doctor!</h4>
      <h4 class="mb-5">What are the different kinds of doctors?</h4>

      <p class="mb-5">
        In Australia, when people think about a ‘doctor’, they’re probably referring to one of two
        main types of doctors:
      </p>
      <h4 class="mb-5">General Practitioners (GPs)</h4>
      <p class="mb-5">
        GPs are usually people’s first port of call when it comes to seeking a diagnosis or
        treatment when they are feeling ill. You might even be lucky enough to have a GP that your
        entire family goes to see.
      </p>
      <p class="mb-5">
        General Practitioners and Resident Medical Officers diagnose and treat physical and mental
        illnesses, disorders and injuries, recommend preventative action and refer patients to
        specialist Medical Practitioners, other health care workers, and social, welfare and support
        workers. (source: JobOutlook.gov.au)
      </p>
      <h4 class="mb-5">Specialist Physician</h4>
      <p class="mb-5">
        Specialists study further to diagnose and treat human disorders and diseases using
        specialist testing, diagnostic and medical techniques.
      </p>
      <p class="mb-5">
        In Australia, specialists receive patients via referrals from General Medical Practitioners
        (GPs) and other medical specialists.
      </p>
      <p class="mb-5">Some of the most common specialists include:</p>

      <ul class="default-list">
        <li>Cardiologists</li>
        <li>Clinical Haematologists</li>
        <li>Medical Oncologists</li>
        <li>Endocrinologists</li>
        <li>Gastroenterologists</li>
        <li>Intensive Care Specialists</li>
        <li>Neurologists</li>
        <li>Paediatricians</li>
        <li>Renal Medicine Specialists</li>
        <li>Rheumatologists</li>
        <li>Thoracic Medicine Specialists</li>
      </ul>

      <h5 class="mb-5">What do you need to become a doctor?</h5>
      <p class="mb-5">
        Different universities have different criteria for entering into a Doctor Of Medicine, but
        if you are planning to get into medicine from Year 12, there is a three-step process:
      </p>

      <ol class="numbered-list">
        <li>
          Meet the university’s specific ATAR requirements. Some universities also ask for certain
          marks in particular subjects as a prerequisite. For instance, the University of Sydney
          requires a minimum of a Band 4 in Advanced Mathematics.<br />
          You should check your preferred university’s website for their full selection criteria.
        </li>
        <li>
          Achieve a specific score in the UCAT (the University Clinical Aptitude Test). You can take
          a look at some of the practice questions on the UCAT site here.
        </li>
        <li>Pass an interview with the University</li>
      </ol>

      <p class="mb-5">
        You can also enter into a Medicine program as a post-graduate, meaning that you have already
        completed a Bachelor’s Degree.
      </p>
      <p class="mb-5">
        In that case you would need to sit through the Graduate Australian Medical School Admissions
        Test (GAMSAT).
      </p>
      <p class="mb-5">Once you have completed your Doctor of Medicine, you will then need to:</p>

      <ul class="default-list">
        <li>
          Complete an intern year (usually through a placement at a clinic, practice or hospital)
        </li>
        <li>Gain general medical registration through the Medical Board of Australia</li>
        <li>
          Complete one or more years of prevocational training (as a Resident Medical Officer)
        </li>
      </ul>

      <p class="mb-5">
        If you want to specialise, you will then have to apply for admission into a medical
        specialty training program via speciality colleges.
      </p>
      <p class="mb-5">
        These vocational training programs can vary in length from three to seven years, after which
        you will be able to practice medicine independently as a general practitioner or specialist
        physician or surgeon.
      </p>
      <p class="mb-5">
        Source:
        <a
          href="https://www.sydney.edu.au/medicine-health/news-and-events/2020/03/10/how-to-become-a-doctor.html"
          target="_blank"
          >The University of Sydney</a
        >
      </p>
    </vx-card>

    <vx-card class="mt-base mb-5">
      <h4 class="mb-5">What tasks and responsibilities do doctors have?</h4>
      <h5 class="mb-5">General Practitioners</h5>

      <p class="mb-5"><strong>Tasks</strong></p>
      <ul class="default-list">
        <li>
          conducting examinations and questioning patients to determine the nature of disorders and
          illnesses, and recording patients' medical information
        </li>
        <li>
          ordering laboratory tests, X-rays and other diagnostic procedures, and interpreting
          findings to assist in diagnosis
        </li>
        <li>
          providing overall care for patients, and prescribing and administering treatments,
          medications and other remedial measures
        </li>
        <li>monitoring patients' progress and response to treatment</li>
        <li>
          advising on diet, exercise and other habits which aid prevention and treatment of disease
          and disorders
        </li>
        <li>
          referring patients to, and exchanging medical information with, specialist Medical
          Practitioners
        </li>
        <li>reporting births, deaths and notifiable diseases to government authorities</li>
        <li>arranging the admission of patients to hospitals</li>
      </ul>

      <p class="mb-5">(source: JobOutlook.gov.au)</p>

      <centered-image-wrapper
        :image="image1"
        classes="2/3"
        alt="General Practitioners and Resident Medical Officers job outlook: $2,459 weekly pay, very strong future growth, 68,200 workers, very high skill level, 76% full-time, 46 hours average full-time week, average age 42, 46% female."
      />
      <centered-image-wrapper
        :image="image2"
        classes="2/3"
        alt="Specialist Physicians job outlook: $4,976 weekly pay, strong future growth, 9,600 workers, very high skill level, 79% full-time, 50 hours average full-time week, average age 43, 43% female."
      />

      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Highlights of Becoming a Doctor</h4>
      <h5 class="mb-5">It’s a very important job that is highly valued in our society</h5>

      <p class="mb-5">
        Health is one of the most important aspects in our lives, and for most people, when they
        become unwell their doctor is their first port of call. Without the various range of doctors
        and medical specialists out there, we would live in a very different kind of world.
      </p>
      <p class="mb-5">
        Doctors are also highly valued because their role is to take an interest in your overall
        well being. Doctors don’t just help people with infections or broken bones - they help
        provide guidance, and are often a professional that people look to for advice and for
        comfort when they are going through stressful or difficult situations.
      </p>
      <p class="mb-5">
        Anyone who has been quite unwell themselves or has had a doctor take care of their loved
        ones knows the value of a skilled medical professional with a compassionate and reassuring
        bedside manner.
      </p>
      <h5 class="mb-5">It’s a highly paid profession</h5>
      <p class="mb-5">
        Because it’s a highly valued profession that needs extensive training and education, doctors
        and medical professionals enjoy a high salary. Typically, the more you specialise as a
        professional, or if you own your own private practice, you can stand to earn more.
      </p>
      <h5 class="mb-5">It’s a highly secure profession that is typically always in-demand</h5>
      <p class="mb-5">
        No matter where people come from or what their background is, people get sick and look for
        medical attention. Like other so-called ‘essential’ professions, there is a very consistent
        demand for professionals working in medicine. Your ability to travel overseas with your
        profession will depend on the country’s local laws and regulations, but as a doctor you
        could work just about anywhere in Australia and be able to make a positive contribution to
        the community.
      </p>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      image: require('@/assets/images/experiences/experience-one.png'),
      image1: require('@/assets/images/experiences/doctor/jo1.png'),
      image2: require('@/assets/images/experiences/doctor/jo2.png'),
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      showVideoPopup: false,
      videoTriggerImg: require('@/assets/images/experiences/nurse/1. Male Nurse.png'),
    };
  },
  provide: function () {
    return {
      openPopup: this.openPopup,
      closePopup: this.closePopup,
    };
  },
  methods: {
    openPopup() {
      this.showVideoPopup = true;
    },
    closePopup() {
      this.showVideoPopup = false;
    },
  },
};
</script>
